import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import API from "../api";

// 🟢 Haptic Feedback Engine
const vibrate = (pattern) => {
  if (typeof window !== "undefined" && navigator.vibrate) {
    navigator.vibrate(pattern);
  }
};

const AdminPromos = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState({
    code: "",
    type: "FLAT",
    value: "",
    minOrderAmount: 200,
    usageLimit: 100,
    expiryDate: ""
  });

  useEffect(() => {
    fetchPromos();
  }, []);

  const fetchPromos = async () => {
    try {
      const { data } = await API.get("/admin/promo");
      setPromos(data);
    } catch (error) {
      toast.error("Failed to load promo codes.");
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.code || !formData.value || !formData.expiryDate) {
      vibrate([100, 50, 100]);
      return toast.error("Please fill all required fields!");
    }

    try {
      setLoading(true);
      await API.post("/admin/promo", formData);
      
      vibrate([50, 50, 50]); // Success burst
      toast.success("Promo Code Created!");
      
      // Reset form and refresh list
      setFormData({ code: "", type: "FLAT", value: "", minOrderAmount: 200, usageLimit: 100, expiryDate: "" });
      fetchPromos();
      
    } catch (error) {
      vibrate([100, 50, 100]);
      toast.error(error.response?.data?.message || "Failed to create code");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this promo code?")) return;
    
    try {
      await API.delete(`/admin/promo/${id}`);
      vibrate(30);
      toast.success("Promo deleted!");
      setPromos(promos.filter(p => p._id !== id));
    } catch (error) {
      toast.error("Failed to delete promo");
    }
  };

  return (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mt-8">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">🎟️ Manage Promo Codes</h2>

      {/* CREATE PROMO FORM */}
      <form onSubmit={handleSubmit} className="bg-gray-50 p-5 rounded-xl border border-gray-200 mb-8 space-y-4">
        <h3 className="font-bold text-gray-700 mb-2">Create New Code</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1">Code Name</label>
            <input name="code" value={formData.code} onChange={handleChange} placeholder="e.g. WELCOME50" className="w-full p-2.5 rounded-lg border border-gray-300 outline-none uppercase font-bold" required />
          </div>
          
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1">Discount Type</label>
            <select name="type" value={formData.type} onChange={handleChange} className="w-full p-2.5 rounded-lg border border-gray-300 outline-none font-medium bg-white">
              <option value="FLAT">Flat ₹ Discount</option>
              <option value="PERCENTAGE">Percentage % Discount</option>
              <option value="FREE_DELIVERY">Free Delivery</option>
            </select>
          </div>
          
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1">Discount Value</label>
            <input type="number" name="value" value={formData.value} onChange={handleChange} placeholder="e.g. 50" className="w-full p-2.5 rounded-lg border border-gray-300 outline-none font-medium" required={formData.type !== "FREE_DELIVERY"} disabled={formData.type === "FREE_DELIVERY"} />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1">Min Order Amount (₹)</label>
            <input type="number" name="minOrderAmount" value={formData.minOrderAmount} onChange={handleChange} className="w-full p-2.5 rounded-lg border border-gray-300 outline-none font-medium" />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1">Total Usage Limit</label>
            <input type="number" name="usageLimit" value={formData.usageLimit} onChange={handleChange} className="w-full p-2.5 rounded-lg border border-gray-300 outline-none font-medium" />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1">Expiry Date</label>
            <input type="date" name="expiryDate" value={formData.expiryDate} onChange={handleChange} className="w-full p-2.5 rounded-lg border border-gray-300 outline-none font-medium" required />
          </div>
        </div>

        <button type="submit" disabled={loading} className="mt-4 bg-gray-800 text-white px-6 py-2.5 rounded-lg font-bold hover:bg-gray-900 transition-colors active:scale-95">
          {loading ? "Creating..." : "+ Generate Code"}
        </button>
      </form>

      {/* ACTIVE PROMOS TABLE */}
      <h3 className="font-bold text-gray-700 mb-4">Active Promo Codes</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm border-collapse min-w-[600px]">
          <thead>
            <tr className="bg-gray-100 text-gray-600">
              <th className="p-3 rounded-tl-lg font-bold">Code</th>
              <th className="p-3 font-bold">Details</th>
              <th className="p-3 font-bold">Min Order</th>
              <th className="p-3 font-bold">Usage</th>
              <th className="p-3 font-bold">Expires</th>
              <th className="p-3 rounded-tr-lg font-bold text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {promos.length === 0 ? (
              <tr><td colSpan="6" className="p-4 text-center text-gray-500">No promo codes found.</td></tr>
            ) : (
              promos.map((promo) => (
                <tr key={promo._id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="p-3 font-extrabold text-green-700 uppercase">{promo.code}</td>
                  <td className="p-3 font-medium text-gray-700">
                    {promo.type === "FLAT" && `₹${promo.value} OFF`}
                    {promo.type === "PERCENTAGE" && `${promo.value}% OFF`}
                    {promo.type === "FREE_DELIVERY" && `Free Delivery`}
                  </td>
                  <td className="p-3 text-gray-600">₹{promo.minOrderAmount}</td>
                  <td className="p-3 text-gray-600">
                    <span className="font-bold text-gray-800">{promo.usedCount}</span> / {promo.usageLimit}
                  </td>
                  <td className="p-3 text-gray-600">
                    {new Date(promo.expiryDate).toLocaleDateString('en-IN')}
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => handleDelete(promo._id)} className="text-red-500 font-bold hover:underline text-xs bg-red-50 px-3 py-1.5 rounded-md">
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminPromos;